import { useCallback, useState } from "react";

import logger from "@app/utils/logger";

interface UseVideoControlsOptions {
  autoPlay?: boolean;
  pauseOnMinimize?: boolean;
}

const useVideoControls = (
  video: HTMLVideoElement | null,
  opts?: UseVideoControlsOptions
) => {
  const [paused, setPaused] = useState(!opts?.autoPlay);
  const [muted, setMuted] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [expanded, setExpanded] = useState(false);

  const pause = useCallback(() => {
    if (!video || video.paused) return;
    video.pause();
    setPaused(true);
  }, [video]);

  const play = useCallback(
    (startTimestamp?: number, onSuccess?: () => void) => {
      if (!video || !video.paused) return;

      if (startTimestamp != null) {
        jumpToTimestamp(startTimestamp);
      }

      try {
        video.play().then(() => {
          onSuccess?.();
          setPaused(false);
        });
      } catch (error) {
        logger.error("Unable to play video");
        logger.error(error);
      }
    },
    [video]
  );

  const mute = useCallback(() => {
    if (!video) return;
    setMuted(true);
    video.muted = true;
  }, [video]);

  const unmute = useCallback(() => {
    if (!video) return;
    setMuted(false);
    video.muted = false;
  }, [video]);

  const minimize = useCallback(() => {
    setExpanded(false);
    if (opts?.pauseOnMinimize) pause();
  }, [opts?.pauseOnMinimize, pause]);
  const maximize = useCallback(() => setExpanded(true), []);

  const jumpToTimestamp = useCallback(
    (seconds: number) => {
      if (!video) return;
      video.currentTime = seconds;
      setCurrentTime(seconds);
    },
    [video]
  );

  const reset = () => {
    setPaused(!opts?.autoPlay);
    setMuted(false);
    setCurrentTime(0);
    setExpanded(false);
  };

  return {
    expanded,
    paused,
    muted,
    currentTime,
    pause,
    play,
    maximize,
    minimize,
    mute,
    unmute,
    jumpToTimestamp,
    updateTime: setCurrentTime,
    reset,
  };
};

export default useVideoControls;
